import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, RotateCcw } from 'lucide-react';
import { writtenQuestions } from '../data/eu-geographie/written-questions';
import { WrittenQuestion } from '../types';
import { useAuth } from '../hooks/useAuth';
import { logActivity, saveAiFeedback } from '../lib/supabase';
import { callClaude } from '../lib/claude';
import { LoadingSpinner } from './LoadingSpinner';

interface ParsedFeedback {
  PUNKTE: string;
  RICHTIG: string;
  FEHLT: string;
  AUSDRUCK: string;
  GESAMT: string;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function WrittenQuiz() {
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();

  const [quizKey, setQuizKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<ParsedFeedback | null>(null);
  const [rawResponse, setRawResponse] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<{ questionId: string; feedback: ParsedFeedback; raw: string }[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime] = useState(Date.now());

  // KI-Nachfrage nach jeder Frage
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpAnswers, setFollowUpAnswers] = useState<Array<{ question: string; answer: string }>>([]);
  const [isAskingFollowUp, setIsAskingFollowUp] = useState(false);

  // Shuffle questions on mount/restart
  const questions = useMemo<WrittenQuestion[]>(() => {
    return shuffleArray(writtenQuestions).slice(0, 10); // 10 Fragen pro Durchgang
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizKey]);

  const currentQuestion = questions[currentIndex];

  // TODO: Smart Modus - MC-Ergebnisse aus Supabase laden
  // und passende schriftliche Fragen basierend auf falschen Antworten priorisieren
  // const loadWeakAreas = async (userId: string) => {
  //   const quizResults = await getQuizResults(userId);
  //   const wrongCategories = analyzeWrongAnswers(quizResults);
  //   return prioritizeQuestions(writtenQuestions, wrongCategories);
  // };

  // Improved parser that handles various Claude response formats
  const parseResponse = (text: string): ParsedFeedback => {
    const sections: ParsedFeedback = {
      PUNKTE: '',
      RICHTIG: '',
      FEHLT: '',
      AUSDRUCK: '',
      GESAMT: '',
    };

    const labels: (keyof ParsedFeedback)[] = ['PUNKTE', 'RICHTIG', 'FEHLT', 'AUSDRUCK', 'GESAMT'];

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      // Build regex to match until next label or end of string
      const remainingLabels = labels.slice(i + 1);
      const endPattern = remainingLabels.length > 0
        ? `(?=[-•*\\s]*\\**(?:${remainingLabels.join('|')})\\**[:\\s])|$`
        : '$';

      // Match pattern: "LABEL:" or "- LABEL:" or "**LABEL:**" or "• LABEL:" etc.
      const regex = new RegExp(
        `[-•*\\s]*\\**${label}\\**[:\\s]+(.+?)${endPattern}`,
        'si'
      );

      const match = text.match(regex);
      if (match && match[1]) {
        // Clean up the matched content
        let content = match[1].trim();
        // Remove trailing markdown or bullets from next section
        content = content.replace(/[-•*]+\s*$/, '').trim();
        sections[label] = content;
      }
    }

    return sections;
  };

  const callClaudeAPI = async (question: string, expectedAnswer: string, userAnswer: string): Promise<string> => {
    const systemPrompt = `Du bist ein freundlicher Lern-Tutor für Adriana (14, 4. Klasse MS Wien).
Bewerte ihre Antwort auf die Frage. Gib Feedback in genau diesem Format:

PUNKTE: X von Y (wie viele Kernpunkte genannt)
RICHTIG: Was war korrekt
FEHLT: Was fehlt oder was falsch ist. Nenne kurz die korrekte Information.
AUSDRUCK: Kurzer Tipp zur Formulierung (nur wenn nötig, sonst "Kein Tipp nötig")
GESAMT: Ermutigendes Fazit (1 Satz)

Wichtig: Jede Zeile muss mit dem Label beginnen, gefolgt von Doppelpunkt und dem Inhalt.
Sei ermutigend aber ehrlich. Sprache: einfaches Deutsch.`;

    const userMessage = `Frage: ${question}
Erwartete Antwort: ${expectedAnswer}
Adrianas Antwort: ${userAnswer}

Bewerte ihre Antwort im vorgegebenen Format.`;

    const data = await callClaude({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 500,
      messages: [
        {
          role: 'user',
          content: userMessage,
        },
      ],
      system: systemPrompt,
    }, 'written-check');

    const text = data.content[0]?.text || '';

    // Debug logging
    console.log('=== CLAUDE RAW RESPONSE ===');
    console.log(text);
    console.log('=== PARSED SECTIONS ===');
    const parsed = parseResponse(text);
    console.log(parsed);

    return text;
  };

  const handleSubmit = async () => {
    if (!userAnswer.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const aiResponse = await callClaudeAPI(
        currentQuestion.question,
        currentQuestion.expectedAnswer,
        userAnswer
      );

      setRawResponse(aiResponse);
      const parsedFeedback = parseResponse(aiResponse);
      setFeedback(parsedFeedback);
      setResults((prev) => [...prev, { questionId: currentQuestion.id, feedback: parsedFeedback, raw: aiResponse }]);

      // Save to Supabase
      if (user?.id) {
        await saveAiFeedback(user.id, 'written_quiz', aiResponse, parsedFeedback as unknown as Record<string, string>, {
          courseId: courseId || 'eu-geographie',
          question: currentQuestion.question,
          userAnswer: userAnswer,
          scoreInfo: parsedFeedback.PUNKTE || '',
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    // Reset follow-up states
    setFollowUpQuestion('');
    setFollowUpAnswers([]);
    setIsAskingFollowUp(false);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setUserAnswer('');
      setFeedback(null);
      setRawResponse('');
      setError(null);
    } else {
      // Log activity when finished
      if (user?.id && courseId) {
        const elapsedSeconds = Math.round((Date.now() - startTime) / 1000);
        await logActivity(user.id, 'written_quiz', {
          course_id: courseId,
          questions_answered: results.length + 1,
          duration_minutes: Math.ceil(elapsedSeconds / 60),
        });

        // Invalidate Lernstand cache so it shows fresh data
        sessionStorage.removeItem(`lernstand-feedback-${user.id}`);
      }
      setIsFinished(true);
    }
  };

  const restart = () => {
    setQuizKey((k) => k + 1);
    setCurrentIndex(0);
    setUserAnswer('');
    setFeedback(null);
    setRawResponse('');
    setError(null);
    setResults([]);
    setIsFinished(false);
    // Reset follow-up states
    setFollowUpQuestion('');
    setFollowUpAnswers([]);
    setIsAskingFollowUp(false);
  };

  // KI-Nachfrage stellen
  const askFollowUp = async () => {
    if (!followUpQuestion.trim()) return;
    setIsAskingFollowUp(true);

    try {
      const data = await callClaude({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 500,
        system: `Du bist ein freundlicher Lern-Tutor für eine Schülerin (14 Jahre, 4. Klasse Mittelschule Wien).
Sie macht gerade ein Quiz zum Thema EU und hat eine Nachfrage zu einer Frage.

Kontext der Quiz-Frage: "${currentQuestion.question}"
Richtige Antwort: "${currentQuestion.expectedAnswer}"

Antworte kurz und verständlich (max 3-4 Sätze).
Verwende einfaches Deutsch. Sei ermutigend.
Wenn sie etwas fragt das nichts mit dem Thema zu tun hat,
lenke sie freundlich zurück zum Lernstoff.`,
        messages: [
          ...followUpAnswers.flatMap(qa => [
            { role: 'user' as const, content: qa.question },
            { role: 'assistant' as const, content: qa.answer },
          ]),
          { role: 'user' as const, content: followUpQuestion },
        ],
      }, 'written-followup');

      const answer = data.content?.[0]?.text || 'Entschuldigung, ich konnte nicht antworten.';

      setFollowUpAnswers(prev => [...prev, {
        question: followUpQuestion,
        answer
      }]);
      setFollowUpQuestion('');

    } catch (error) {
      console.error('Follow-up API error:', error);
      setFollowUpAnswers(prev => [...prev, {
        question: followUpQuestion,
        answer: 'Fehler bei der Verbindung. Versuch es nochmal!'
      }]);
    }

    setIsAskingFollowUp(false);
  };

  // Check if feedback has any parsed content
  const hasParsedContent = feedback && (
    feedback.PUNKTE || feedback.RICHTIG || feedback.FEHLT || feedback.AUSDRUCK || feedback.GESAMT
  );

  // Finished screen
  if (isFinished) {
    return (
      <div className="written-quiz-result">
        <h1>✍️ Schriftliches Quiz fertig!</h1>
        <p className="result-subtitle">Du hast {results.length} Fragen beantwortet</p>

        <div className="result-summary">
          {results.map((result, index) => {
            const question = questions.find((q) => q.id === result.questionId);
            return (
              <div key={result.questionId} className="result-item">
                <div className="result-item-header">
                  <span className="result-number">Frage {index + 1}</span>
                  <span className="result-points">{result.feedback.PUNKTE || '—'}</span>
                </div>
                <p className="result-question">{question?.question}</p>
                <p className="result-feedback">{result.feedback.GESAMT || result.raw.slice(0, 150) + '...'}</p>
              </div>
            );
          })}
        </div>

        <div className="result-actions">
          <button className="btn-primary" onClick={restart}>
            <RotateCcw size={18} />
            Nochmal üben
          </button>
          <Link to={`/course/${courseId}`} className="btn-secondary">
            Zurück zum Kurs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="written-quiz">
      <div className="written-header">
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
        <span className="written-badge">✍️ Schriftlich</span>
      </div>

      <div className="written-progress">
        Frage {currentIndex + 1} von {questions.length}
      </div>

      <div className="written-question">
        <h2>{currentQuestion.question}</h2>
        <span className="points-hint">({currentQuestion.maxPoints} Punkte möglich)</span>
      </div>

      <div className="written-answer">
        <textarea
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          placeholder="Schreibe deine Antwort hier..."
          rows={6}
          disabled={isSubmitting || !!feedback}
          className={feedback ? 'disabled' : ''}
        />
      </div>

      {error && (
        <div className="written-error">
          <span>⚠️ {error}</span>
        </div>
      )}

      {isSubmitting && <LoadingSpinner text="KI bewertet..." />}

      {!feedback && (
        <div className="written-actions">
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={!userAnswer.trim() || isSubmitting}
          >
            <Send size={18} />
            Abschicken
          </button>
        </div>
      )}

      {feedback && (
        <div className="ai-feedback">
          <h3>KI-Feedback</h3>

          {/* Punkte */}
          {feedback.PUNKTE && (
            <div style={{
              background: 'rgba(255, 213, 79, 0.15)',
              borderLeft: '4px solid #ffd54f',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ffd54f', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                ⭐ PUNKTE
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {feedback.PUNKTE}
              </div>
            </div>
          )}

          {/* Richtig */}
          {feedback.RICHTIG && (
            <div style={{
              background: 'rgba(76, 175, 80, 0.15)',
              borderLeft: '4px solid #4caf50',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#4caf50', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                ✅ RICHTIG
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {feedback.RICHTIG}
              </div>
            </div>
          )}

          {/* Fehlt */}
          {feedback.FEHLT && feedback.FEHLT.toLowerCase() !== 'nichts' && (
            <div style={{
              background: 'rgba(255, 152, 0, 0.15)',
              borderLeft: '4px solid #ff9800',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ff9800', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                📝 FEHLT
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {feedback.FEHLT}
              </div>
            </div>
          )}

          {/* Ausdruck */}
          {feedback.AUSDRUCK && feedback.AUSDRUCK.toLowerCase() !== 'kein tipp nötig' && (
            <div style={{
              background: 'rgba(66, 165, 245, 0.15)',
              borderLeft: '4px solid #42a5f5',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#42a5f5', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                💡 AUSDRUCK
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {feedback.AUSDRUCK}
              </div>
            </div>
          )}

          {/* Gesamt/Fazit */}
          {feedback.GESAMT && (
            <div style={{
              background: 'rgba(171, 71, 188, 0.15)',
              borderLeft: '4px solid #ab47bc',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ab47bc', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                🎯 FAZIT
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {feedback.GESAMT}
              </div>
            </div>
          )}

          {/* Fallback: Show raw response if nothing was parsed */}
          {!hasParsedContent && rawResponse && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#9fa8da', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                📋 Feedback
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {rawResponse}
              </div>
            </div>
          )}

          {/* Perfekte Antwort zum Lernen */}
          <div style={{
            background: 'rgba(76, 175, 80, 0.1)',
            border: '2px solid rgba(76, 175, 80, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            marginTop: '16px',
          }}>
            <div style={{
              color: '#4caf50',
              fontWeight: 700,
              fontSize: '14px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              📖 Perfekte Antwort zum Lernen:
            </div>
            <div style={{
              color: '#e8eaf6',
              fontSize: '15px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
            }}>
              {currentQuestion.expectedAnswer}
            </div>
          </div>

          {/* KI-Nachfrage Bereich */}
          <div style={{
            background: 'rgba(66, 165, 245, 0.08)',
            border: '1px solid rgba(66, 165, 245, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            marginTop: '16px',
            marginBottom: '12px',
          }}>
            <div style={{
              color: '#42a5f5',
              fontSize: '14px',
              fontWeight: 700,
              marginBottom: '12px'
            }}>
              🤖 Hast du noch eine Frage dazu?
            </div>

            {/* Bisherige Nachfragen anzeigen */}
            {followUpAnswers.map((qa, index) => (
              <div key={index} style={{ marginBottom: '12px' }}>
                <div style={{
                  background: 'rgba(255, 213, 79, 0.1)',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  marginBottom: '6px',
                  fontSize: '14px',
                  color: '#ffd54f',
                }}>
                  💬 {qa.question}
                </div>
                <div style={{
                  background: 'rgba(66, 165, 245, 0.1)',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  color: '#e8eaf6',
                  lineHeight: '1.5',
                }}>
                  🤖 {qa.answer}
                </div>
              </div>
            ))}

            {/* Eingabefeld */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={followUpQuestion}
                onChange={(e) => setFollowUpQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && followUpQuestion.trim()) {
                    askFollowUp();
                  }
                }}
                placeholder="z.B. Was ist der Unterschied zwischen..."
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  color: '#e8eaf6',
                  fontSize: '14px',
                  padding: '12px',
                  fontFamily: 'Nunito, sans-serif',
                }}
              />
              <button
                onClick={askFollowUp}
                disabled={!followUpQuestion.trim() || isAskingFollowUp}
                style={{
                  background: followUpQuestion.trim() && !isAskingFollowUp ? '#42a5f5' : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '10px',
                  color: followUpQuestion.trim() && !isAskingFollowUp ? '#fff' : 'rgba(255,255,255,0.3)',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: followUpQuestion.trim() && !isAskingFollowUp ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}
              >
                {isAskingFollowUp ? '...' : '🤖'}
              </button>
            </div>
          </div>

          <button className="btn-primary" onClick={handleNext} style={{ width: '100%', marginTop: '16px' }}>
            {currentIndex < questions.length - 1 ? 'Nächste Frage' : 'Ergebnis anzeigen'}
          </button>
        </div>
      )}
    </div>
  );
}
